import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, BookOpen, Headphones, Clock, Mail, ShieldCheck } from "lucide-react";
import { API } from "../lib/api";

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 20; // ~1 minute — long enough to confirm payment, not to wait on the report

export default function DiagnosisProSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [polls, setPolls] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/diagnosis-pro/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        setPolls((p) => p + 1);
        // Once paid is confirmed, stop polling — we do NOT wait for the report here.
        if (data.payment_status === "paid" || polls > MAX_POLLS) return;
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS * 2);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId, polls]);

  const paid = status.payment_status === "paid";

  return (
    <main
      className="min-h-screen bg-bg text-ink flex items-center justify-center px-6 py-24 relative overflow-hidden"
      data-testid="dp-success"
    >
      {/* Soft atmosphere */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 top-16 h-[520px] w-[520px] rounded-full opacity-[0.05] blur-3xl bg-[radial-gradient(circle,#B08D57,transparent_70%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full opacity-[0.05] blur-3xl bg-[radial-gradient(circle,#2B4A3B,transparent_70%)]"
      />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-2xl w-full text-center relative z-10"
      >
        {paid ? (
          <>
            {/* Confirmed */}
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="mx-auto h-16 w-16 rounded-full bg-forest flex items-center justify-center shadow-[0_8px_30px_-6px_rgba(43,74,59,0.5)]"
            >
              <Check className="h-7 w-7 text-bg" />
            </motion.div>
            <p className="mt-8 font-sans text-[11px] uppercase tracking-mega text-brass">
              Payment received · Video in the works
            </p>
            <h1
              className="mt-4 font-serif text-4xl md:text-6xl text-ink leading-[1.05]"
              data-testid="dp-success-title"
            >
              Thank you.
              <br />
              <span className="italic text-forest">Your Diagnosis Pro video is being recorded.</span>
            </h1>
            <p className="mt-8 font-serif text-lg md:text-xl text-ink-muted leading-relaxed max-w-xl mx-auto">
              Krishna will research your business and record a personalised video
              walkthrough. It will land in your inbox within 48 hours.
            </p>

            {/* Refund guarantee */}
            <div className="mt-8 mx-auto max-w-xl rounded-2xl border border-brass/40 bg-brass/[0.05] px-5 py-4 flex items-start gap-3 text-left" data-testid="dp-success-guarantee">
              <ShieldCheck className="h-5 w-5 text-brass shrink-0 mt-0.5" />
              <p className="font-serif text-base md:text-lg text-ink leading-snug">
                <span className="font-sans text-[10px] uppercase tracking-mega text-brass block mb-1">100% refund guarantee</span>
                If the video is not worth $499, reply to the delivery email within seven days and we refund you in full. No questions asked.
              </p>
            </div>

            {/* Two info chips */}
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2 font-sans text-[11px] uppercase tracking-mega text-ink-muted">
                <Clock className="h-3.5 w-3.5 text-brass" />
                Delivered within 48 hours
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2 font-sans text-[11px] uppercase tracking-mega text-ink-muted">
                <Mail className="h-3.5 w-3.5 text-brass" />
                Emailed to you directly
              </span>
            </div>

            {/* While-you-wait */}
            <div className="mt-16 text-left">
              <p className="font-sans text-[11px] uppercase tracking-mega text-brass mb-4 text-center">
                While you wait
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link
                  to="/story"
                  data-testid="dp-success-read-book"
                  className="group rounded-2xl border border-line bg-surface p-6 hover:border-forest/60 transition-all"
                >
                  <BookOpen className="h-5 w-5 text-brass mb-3" />
                  <h3 className="font-serif text-2xl text-ink leading-tight">
                    Read the little book
                  </h3>
                  <p className="mt-2 font-serif italic text-base text-ink-muted leading-relaxed">
                    A short cinematic story about the practice. Fifteen minutes.
                  </p>
                  <span className="mt-4 inline-block font-sans text-[11px] uppercase tracking-mega text-forest group-hover:underline">
                    Open the book →
                  </span>
                </Link>

                <Link
                  to="/field-notes"
                  data-testid="dp-success-listen-notes"
                  className="group rounded-2xl border border-line bg-surface p-6 hover:border-forest/60 transition-all"
                >
                  <Headphones className="h-5 w-5 text-brass mb-3" />
                  <h3 className="font-serif text-2xl text-ink leading-tight">
                    Listen to Field Notes
                  </h3>
                  <p className="mt-2 font-serif italic text-base text-ink-muted leading-relaxed">
                    Sunday letters, narrated in Krishna&apos;s voice. Two minutes each.
                  </p>
                  <span className="mt-4 inline-block font-sans text-[11px] uppercase tracking-mega text-forest group-hover:underline">
                    Open Field Notes →
                  </span>
                </Link>
              </div>
            </div>

            <p className="mt-12 font-serif italic text-sm text-ink-muted">
              We&apos;ll email you the moment your video is ready. No need to refresh this page.
            </p>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-8 w-8 text-forest animate-spin" />
            <p className="mt-8 font-sans text-[11px] uppercase tracking-mega text-brass">
              Confirming your payment
            </p>
            <h1 className="mt-4 font-serif text-3xl md:text-5xl text-ink italic leading-tight">
              Just a moment.
            </h1>
            <p className="mt-6 font-serif text-lg text-ink-muted">
              We&apos;re hearing back from Stripe. This usually takes a few seconds.
            </p>
          </>
        )}
      </motion.div>
    </main>
  );
}
